import { FileItem, FolderItem, Location } from "@/types/fse";
import { Evidence, Highlight, SpreadsheetTarget } from "@/types/evidences";

export type BoundingRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type HighlightInfo = {
  page: number;
  boundingRect: BoundingRect;
};

export type CitationInfo = {
  citationId: string;
  page?: number;
  boundingRect?: BoundingRect;
  highlights: HighlightInfo[];
  activeHighlightIndex: number;
  fseId: string;
  documentId: string;
  citation: number | string;
  index: number;
  spreadsheetTarget?: SpreadsheetTarget;
  sourceDocumentContentType?: string | null;
};

export type { FileItem, FolderItem, Location, Evidence, Highlight };

export type FlattenedItem = (FileItem | FolderItem) & { depth: number; isExpanded: boolean };

export interface ChatMessage {
  id: string;
  content: string;
  type: "human" | "ai";
  timestamp?: Date;
}

export type AnswerSource = "history" | "documents";

// Stage of a pending answer, announced by the Q&A decision graph (backend QaStep).
export const QA_PROGRESS_STEPS = [
  "understanding",
  "reviewing_history",
  "rewriting",
  "planning",
  "selecting_documents",
  "searching",
  "generating",
] as const;

export type QaProgressStep = (typeof QA_PROGRESS_STEPS)[number];

export const isQaProgressStep = (value: unknown): value is QaProgressStep =>
  QA_PROGRESS_STEPS.includes(value as QaProgressStep);

export interface StreamMessage extends ChatMessage {
  isUser: boolean;
  answerSource?: AnswerSource;
  progressStep?: QaProgressStep;
}

export interface Artefact {
  id: string;
  name: string;
  content: string;
  type?: string;
}

export const isLetterArtefact = (artefact: Artefact): boolean =>
  artefact.type === "letter" || artefact.type === "email";

export interface InterruptOption {
  document_id: string;
  name: string;
  summary: string;
}

export interface InterruptPayload {
  kind: string;
  prompt: string;
  options: InterruptOption[];
}

export interface PendingInterrupt {
  queryId: string;
  payload: InterruptPayload;
}

export interface WebSocketMessage {
  ev_type:
    | "query_started"
    | "query_token"
    | "query_evidence"
    | "query_eot"
    | "query_interrupt"
    | "query_history_token_warning"
    | "query_large_folder_warning"
    | "query_answer_source"
    | "query_status";
  t?: string;
  query_id?: string;
  citation_id?: string;
  document_id?: string;
  answer_offset_start?: number;
  answer_offset_end?: number;
  fse_id?: string;
  highlights?: Highlight[];
  payload?: InterruptPayload;
  tokens?: number;
  count?: number;
  source?: AnswerSource;
  step?: string;
}
